<!-- eslint-disable no-console -->
<!-- eslint-disable n/prefer-global/process -->
<script setup lang="ts">
import _ from '@lodash'
import {
  useAdditionalStore,
  useAttractionsStore,
  useAuthStore,
  useAwardsStore,
  useDiningsStore,
  useEventsStore,
  useFacilitiesStore,
  useGalleriesStore,
  useHotelStore,
  useLabelsStore,
  useMeetingsStore,
  useNewsStore,
  usePagesStore,
  useRecruitmentStore,
  useRoomsStore,
  useSettingStore,
  useSlideshowStore,
  useSocialMediaStore,
  useTemplatesStore,
  useTermsStore,
} from 'pinia-store'
import { convertHex } from '@ui/helpers/functions'
import type { IPagesParams } from '@ui/interfaces/pages'
import { computed, defineAsyncComponent, watch } from 'vue'
import { useFavicon } from '@vueuse/core'
import { getDataLang, getHeaders, getQuery } from 'pinia-store/helpers/api'
import * as localStorage from './helpers/local-storage'
import { templates } from './constants/templates'
import { useGtag, useI18n, useLocaleHead, useNuxtApp, useRoute, useRouter } from '#imports'

const { gtag } = useGtag()

const i18nHead = useLocaleHead({
  addDirAttribute: true,
  addSeoAttributes: true,
})

const { $dayjs: dayjs }: any = useNuxtApp()

const route = useRoute()
const router = useRouter()
const routers = computed(() => router?.options?.routes || [])
const useSetting = useSettingStore()
const useAuth = useAuthStore()
const useHotel = useHotelStore()
const useTemplates = useTemplatesStore()
const usePages = usePagesStore()
const useSocialMedia = useSocialMediaStore()
const useAwards = useAwardsStore()
const useLabels = useLabelsStore()
const useSlideshow = useSlideshowStore()
const useRooms = useRoomsStore()
const useMeetings = useMeetingsStore()
const useEvents = useEventsStore()
const useFacilties = useFacilitiesStore()
const useGalleries = useGalleriesStore()
const useDinings = useDiningsStore()
const useRecruitment = useRecruitmentStore()
const useAttractions = useAttractionsStore()
const useAdditional = useAdditionalStore()
const useNews = useNewsStore()
const useTerms = useTermsStore()

// --- START --- set Default language from setting to i18n
const { locale } = useI18n()
const { setLocale } = useI18n()
const currentLang = ref(locale)
currentLang.value = useSetting.getLang
setLocale(currentLang.value)
// --- END --- set Default language from setting to i18n

const template: string = useSetting.template
function setSpaceToDash(val: string, operator: string = '-') {
  if (!val)
    return
  return val?.toString().replace(/\s+/g, operator).toLowerCase()
}
const headers = getHeaders()
const query = getQuery()
const hotel_id = query?.hotel_id
let lang_id = Number(useSetting.lang_id)
let lang = route?.params?.language || useSetting.lang
let cssVars = {}
const storage = useAuth.getStorage
const expired_date = storage?.expired_date ? storage.expired_date : null
const now = dayjs()
// Languages
const { data: dataLanguages, error: errorLanguages } = await fetchLanguages(headers, { hotel_id })
const tmpLang = dataLanguages?.value?.data?.languages
if (errorLanguages.value) {
  console.log('[languages]', 'get data LANGUAGES failed...', errorLanguages)
}
else if (tmpLang) {
  useSetting.setLanguages(tmpLang)
  const languages = _.cloneDeep(tmpLang)
  const tmp = languages.filter((el: {
    label_id: number
    label_uri: string
    label_name: string
    iso_code: string
    is_default: boolean
  }) => el?.label_uri === lang)
  if (tmp && tmp.length) {
    lang_id = tmp[0].label_id
    lang = tmp[0].label_uri
    query.lang_id = lang_id
  }
}
if (!expired_date || (expired_date && !dayjs(expired_date).isAfter(now)))
  useAuth.setData({ showData: false })

const { data: dataTemplates, error: errorTemplates } = await fetchTemplates(headers, {
  lang_id,
})
if (errorTemplates.value) {
  console.log('[templates] get template SETTINGS failed...', errorTemplates)
}
else {
  const defaultTemplates = template && templates[template.toUpperCase()] ? templates[template.toUpperCase()] : templates.LSBD

  const tmpTemplates = dataTemplates.value?.data
  if (defaultTemplates && typeof defaultTemplates === 'object' && Object.keys(defaultTemplates)) {
    cssVars = {
      '--font-primary': defaultTemplates.fontPrimary,
      '--font-secondary': defaultTemplates.fontSecondary,
      '--font-color-primary': defaultTemplates.primaryFontColor,
      '--font-color-secondary': defaultTemplates.secondaryFontColor,
      '--color-primary': defaultTemplates.colorPrimary,
      '--color-secondary': defaultTemplates.colorSecondary,
      '--color-primary-rgb': convertHex(defaultTemplates.colorPrimary),
      '--color-secondary-rgb': convertHex(defaultTemplates.colorSecondary),
    }
  }

  if (tmpTemplates?.primary?.font_primary)
    cssVars['--font-primary'] = tmpTemplates.primary?.font_provider === 'adobe' ? setSpaceToDash(tmpTemplates.primary.font_primary) : tmpTemplates.primary.font_primary
  else if (tmpTemplates?.primary?.font_name)
    cssVars['--font-primary'] = tmpTemplates.primary?.font_provider === 'adobe' ? setSpaceToDash(tmpTemplates.primary.font_name) : tmpTemplates.primary.font_name
  else if (tmpTemplates?.font_primary)
    cssVars['--font-primary'] = tmpTemplates.font_primary
  else if (tmpTemplates?.font_name)
    cssVars['--font-primary'] = tmpTemplates.font_name

  if (tmpTemplates?.secondary?.font_secondary)
    cssVars['--font-primary'] = tmpTemplates.secondary?.font_provider === 'adobe' ? setSpaceToDash(tmpTemplates.secondary.font_secondary) : tmpTemplates.secondary.font_secondary
  else if (tmpTemplates?.secondary?.font_name)
    cssVars['--font-primary'] = tmpTemplates.secondary?.font_provider === 'adobe' ? setSpaceToDash(tmpTemplates.secondary.font_name) : tmpTemplates.secondary.font_name
  else if (tmpTemplates?.font_secondary)
    cssVars['--font-secondary'] = tmpTemplates.font_secondary

  if (tmpTemplates?.primary.background_color) {
    cssVars['--color-primary'] = tmpTemplates.primary.background_color
    cssVars['--color-primary-rgb'] = convertHex(tmpTemplates.primary.background_color)
  }
  else if (tmpTemplates?.background_color) {
    cssVars['--color-primary'] = tmpTemplates.background_color
    cssVars['--color-primary-rgb'] = convertHex(tmpTemplates.background_color)
  }
  if (tmpTemplates?.secondary.background_color) {
    cssVars['--color-secondary'] = tmpTemplates.secondary.background_color
    cssVars['--color-secondary-rgb'] = convertHex(tmpTemplates.secondary.background_color)
  }
  else if (tmpTemplates?.background_color) {
    cssVars['--color-secondary'] = tmpTemplates.background_color
    cssVars['--color-secondary-rgb'] = convertHex(tmpTemplates.background_color)
  }

  if (tmpTemplates?.primary?.font_url)
    cssVars['--font-primary-url'] = tmpTemplates.primary.font_url
  else if (tmpTemplates?.font_url)
    cssVars['--font-primary-url'] = tmpTemplates.font_url

  if (tmpTemplates?.secondary?.font_url)
    cssVars['--font-secondary-url'] = tmpTemplates.secondary.font_url
  else if (tmpTemplates?.font_url)
    cssVars['--font-secondary-url'] = tmpTemplates.font_url

  if (tmpTemplates?.primary?.font_color)
    cssVars['--font-color-primary'] = tmpTemplates.primary.font_color
  else if (tmpTemplates?.font_color)
    cssVars['--font-color-primary'] = tmpTemplates.font_color

  if (tmpTemplates?.secondary?.font_color)
    cssVars['--font-color-secondary'] = tmpTemplates.secondary.font_color
  else if (tmpTemplates?.font_color)
    cssVars['--font-color-secondary'] = tmpTemplates.font_color

  if (tmpTemplates && typeof tmpTemplates === 'object' && Object.entries(tmpTemplates).length > 0)
    useTemplates.setDetail(tmpTemplates)
  useSetting.setCssVars(cssVars)
}
// fancy console
if (process.client) {
  // eslint-disable-next-line no-console
  console.log(
    `%c✨ Copyright © ${new Date().getFullYear()}, Powered by Archipelago (pba.archipelagointernational.com) ✨`,
    `background:rgb(${useSetting.cssVars['--color-primary-rgb']});
    color:#fff;
    font-family:sans-serif;
    font-size:16px;font-weight:bold;padding:4px;margin:16px0;border-radius:4px;text-align:center;`,
  )
}

// Additional pages
let dataAdditional
const { data: dataAdditionalPage, error: errorAdditionalPage } = await fetchAdditionalPage(headers, {
  hotel_id,
})
if (errorAdditionalPage.value) {
  console.log('[additional_page]', 'get data ADDITIONAL PAGE failed...', errorAdditionalPage)
}
else if (dataAdditionalPage.value?.data?.additional_pages) {
  dataAdditional = dataAdditionalPage.value.data.additional_pages
  useAdditional.setList(dataAdditional)
}

// general terms pages
let dataGeneralTerms
const { data: dataGeneralTermsPage, error: errorGeneralTermsPage } = await fetchGeneralTermsPage(headers, {
  hotel_id,
})
if (errorGeneralTermsPage.value) {
  console.log('[additional_page]', 'get data ADDITIONAL PAGE failed...', errorGeneralTermsPage)
}
else if (dataGeneralTermsPage.value?.data?.list) {
  dataGeneralTerms = dataGeneralTermsPage.value.data.list
  useTerms.setList(dataGeneralTerms)
}

// pages
const { data: dataPages, error: errorPages } = await fetchPages(headers, {
  hotel_id,
})
if (errorPages.value) {
  console.log('[pages]', 'get data PAGES failed...', errorPages)
}
else {
  const tmp: IPagesParams[] = dataPages.value?.data?.pages || []
  usePages.setList(tmp, dataAdditional)
}

// hotel
const { data: dataHotel, error: errorHotel } = await fetchHotel(headers, {
  lang_id,
})
if (errorHotel.value) {
  console.log('[hotels]', 'get data HOTEL failed...', errorHotel)
}
else if (dataHotel.value?.data?.profile) {
  const tmp = dataHotel.value.data.profile
  const tmpAnnouncement = dataHotel.value.data?.announcement
  if (tmp.gtag_id)
    gtag('config', tmp.gtag_id)

  // set favicon here
  const favicon = tmp.favicon || tmp.hotel_logo_color || tmp.hotel_logo
  useFavicon(favicon)
  useHotel.setProfile(tmp)
  if (tmpAnnouncement && tmpAnnouncement.length)
    useHotel.setAnnouncement(tmpAnnouncement)
}
// social-media
const { data: dataSocialMedia, error: errorSocialMedia } = await fetchSocialMedia(headers, query)
if (errorSocialMedia.value) {
  console.log('[social_medias]', 'get data SOCIAL MEDIA failed...', errorSocialMedia)
}
else if (dataSocialMedia.value?.data?.social_media) {
  const tmp = dataSocialMedia.value.data.social_media
  useSocialMedia.setList(tmp)
}
// awards
const { data: dataAwards, error: errorAwards } = await fetchAwards(headers, query)
if (errorAwards.value) {
  console.log('[awards]', 'get data AWARDS failed...', errorAwards)
}
else if (dataAwards.value?.data?.awards) {
  const tmp = dataAwards.value.data.awards
  useAwards.setList(tmp)
}
// labels all
const { data: dataAllLabels, error: errorAllLabels } = await fetchAllLabels(headers, {
  hotel_id,
})
if (errorAllLabels.value) {
  console.log('[labels]', 'get data LABELS failed...', errorAllLabels)
}
else if (dataAllLabels.value?.data) {
  const tmp = dataAllLabels.value.data
  useLabels.setLabels(tmp)
  if (getDataLang(tmp)[0]?.HOME_BACKGROUND_PATTERN)
    cssVars['--background-pattern'] = getDataLang(tmp)[0]?.HOME_BACKGROUND_PATTERN
  useSetting.setCssVars(cssVars)
}

const { data: dataFacilities, error: errorFacilities } = await fetchFacilities(headers, {
  hotel_id,
})
if (errorFacilities.value) {
  console.log('[facility_page]', 'get data FACILITY PAGE failed...', errorFacilities)
}
else if (dataFacilities.value?.data?.facility) {
  const tmp = dataFacilities.value.data.facility
  useFacilties.setList(tmp)
}

const { data: dataRooms, error: errorRooms } = await fetchRooms(headers, {
  hotel_id,
})
if (errorRooms.value) {
  console.log('[rooms_page]', 'get data ROOMS PAGE failed...', errorRooms)
}
else if (dataRooms.value?.data?.rooms) {
  const tmp = dataRooms.value.data
  useRooms.setList(tmp, true)
}

const { data: dataAttraction, error: errorAttraction } = await fetchAttraction(headers, {
  hotel_id,
})
if (errorAttraction.value) {
  console.log('[attraction_page]', 'get data ATTRACTION PAGE failed...', errorAttraction)
}
else if (dataAttraction.value?.data?.attraction) {
  const tmp = dataAttraction.value.data.attraction
  useAttractions.setList(tmp)
}

const { data: dataEvents, error: errorEvents } = await fetchEvents(headers, {
  hotel_id,
})
if (errorEvents.value) {
  console.log('[events_page]', 'get data EVENTS PAGE failed...', errorEvents)
}
else if (dataEvents.value?.data?.event) {
  const tmp = dataEvents.value.data.event
  useEvents.setList(tmp)
}

const { data: dataMeetings, error: errorMeetings } = await fetchMeetings(headers, {
  hotel_id,
})
if (errorMeetings.value) {
  console.log('[meetings_page]', 'get data MEETINGS PAGE failed...', errorMeetings)
}
else if (dataMeetings.value?.data?.meeting) {
  const tmp = dataMeetings.value.data.meeting
  useMeetings.setList(tmp)
}

const { data: dataNews, error: errorNews } = await fetchNews(headers, {
  hotel_id,
})
if (errorNews.value) {
  console.log('[news_page]', 'get data NEWS PAGE failed...', errorNews)
}
else if (dataNews.value?.data?.press) {
  const tmp = dataNews.value.data.press
  useNews.setDataHidden(tmp)
}

const { data: dataDinings, error: errorDinings } = await fetchDinings(headers, {
  hotel_id,
})
if (errorDinings.value) {
  console.log('[dinings_page]', 'get data DINING PAGE failed...', errorDinings)
}
else if (dataDinings.value?.data?.dining) {
  const tmp = dataDinings.value.data.dining
  useDinings.setList(tmp)
}

const { data: dataSlideshow, error: errorSlideshow } = await fetchSlideshow(headers, {
  lang_id,
})

if (errorSlideshow.value) {
  console.log('[slideshow]', 'get data SLIDESHOW failed...', errorSlideshow)
}
else if (dataSlideshow.value?.data?.slideshow) {
  const tmp = dataSlideshow.value.data.slideshow
  useSlideshow.setList(tmp)
}

const { data: dataGalleries, error: errorGalleries } = await fetchGalleries(headers, {
  lang_id,
})
if (errorGalleries.value) {
  console.log('[galleries]', 'get data GALLERIES failed...', errorGalleries)
}
else if (dataGalleries.value?.data?.gallery) {
  const tmp = dataGalleries.value.data.gallery
  useGalleries.setList(tmp)
}

const { data: dataRecruitment, error: errorRecruitment } = await fetchRecruitment(headers, query)
if (errorRecruitment.value) {
  console.log('[recruitment_page]', 'get data RECRUITMENT failed...', errorRecruitment)
}
else if (dataRecruitment.value?.data.recruitment) {
  const tmp = dataRecruitment.value.data.recruitment
  useRecruitment.setRecruitment(tmp)
}

// User actions to explicitly choose themes
// function toggleTheme(isDark) {
//   localStorage.theme = isDark ? 'dark' : 'light' // Save preference
//   document.documentElement.classList.toggle('dark', isDark)
// }

watch(
  useTemplates,
  (cur) => {
    localStorage.setItem(
      'useTemplates',
      JSON.stringify({
        detail: cur.detail,
      }),
    )
  },
  { deep: true },
)

const layout = computed(() => {
  return defineAsyncComponent(() => import(`./layouts/${setSpaceToDash(template) ?? 'default'}.vue`))
})
function handleRouter() {
  if (routers.value && routers.value.length && route.name) {
    // let bool = false
    // let name = 'not-found'
    // for (let i = 0; i < routers.value.length; i++) {
    //   const el = routers.value[i]
    //   if (el?.name === route.name) {
    //     bool = true
    //     break
    //   }
    // }
    // if (languages && languages.length && route.path) {
    //   const par = route.path.split('/')
    //   bool = false
    //   for (let i = 0; i < languages.length; i++) {
    //     const value = languages[i]
    //     if (par.length > 1 && par[1].length && value?.label_uri.toLowerCase() === par[1].toLowerCase()) {
    //       bool = true
    //       break
    //     }
    //     else if (!par[1] || value?.label_uri.toLowerCase() !== par[1].toLowerCase()) {
    //       name = 'home'
    //     }
    //   }
    // }
    if (route.name === 'index') {
      return router.push({
        name: 'home',
        params: {
          language: currentLang.value,
        },
        force: true,
      }).then(() => { router.go(0) })
    }
    // else if (!bool) {
    //   return navigateTo({
    //     name,
    //     params: {
    //       language: lang,
    //     },
    //   })
    // }
  }
}
onMounted(async () => {
  await handleRouter()
})
// ** useHead
useHead({
  htmlAttrs: {
    style: useSetting.cssVars,
    lang: i18nHead.value.htmlAttrs.lang,
    dir: i18nHead.value.htmlAttrs.dir,
    class: 'section-html',
  },
  link: [{ rel: 'stylesheet', href: 'https://use.typekit.net/xeu7xfo.css' }],
  meta: [...(i18nHead.value.meta || [])],
  bodyAttrs: {
    class: `${useSetting.template}-${route?.name || 'home'}page primary-${cssVars['--font-primary'].toLowerCase().replace(/\s/g, '-')} secondary-${cssVars['--font-secondary'].toLowerCase().replace(/\s/g, '-')}`,
  },
})
</script>

<!-- <VitePwaManifest /> -->

<template>
  <NuxtLoadingIndicator />
  <EXLayout>
    <component :is="layout">
      <NuxtPage :key="route.path" />
    </component>
  </EXLayout>
</template>

<style lang="scss">
// animation for page transition
.page-enter-active,
.page-leave-active{
  transition: all .5s
}
.page-enter-from,
.page-leave-to {
  filter: blur(1rem);
  opacity: 0;
  transform: translateY(.5rem)
}
</style>
